import { useEffect, useRef, useState } from 'react';
import type { MediaRefreshResponse } from './contracts.js';

/**
 * Rewrite the `t` query parameter of a media URL to the token held now.
 *
 * Every playlist, segment, poster and caption request carries the token in the
 * query string (an HLS player cannot attach an Authorization header to a
 * segment request), so rotating a token is exactly this substitution applied
 * on the way out.
 */
export function withMediaToken(url: string, token: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    if (!parsed.searchParams.has('t')) return url;
    parsed.searchParams.set('t', token);
    return parsed.toString();
  } catch {
    return url;
  }
}

function tokenOf(url: string): string {
  try {
    return new URL(url, window.location.origin).searchParams.get('t') ?? '';
  } catch {
    return '';
  }
}

function refreshUrlFor(hlsUrl: string, token: string): string | null {
  try {
    const parsed = new URL(hlsUrl, window.location.origin);
    if (!/\/hls\/master\.m3u8$/.test(parsed.pathname)) return null;
    parsed.pathname = parsed.pathname.replace(/\/hls\/master\.m3u8$/, '/refresh-token');
    parsed.search = '';
    parsed.searchParams.set('t', token);
    return parsed.toString();
  } catch {
    return null;
  }
}

export type MediaTokenState = {
  /**
   * The token to use for the *next* request. Read through the ref rather than
   * as state so hls.js's loader always sees the current one without the player
   * re-rendering or the media element being touched.
   */
  tokenRef: React.RefObject<string>;
  /** Bumped after each successful rotation. Only the native-HLS path needs it. */
  rotation: number;
  /** Set when rotation failed outright, so the UI can say playback will stop. */
  error: string | null;
};

/**
 * Keep a playable media token in hand.
 *
 * `GET /api/media/:videoId/refresh-token?t=<current>` trades a token that is
 * about to expire for a fresh one, re-running the same entitlement check — so
 * a learner unenrolled mid-lecture cannot extend their own access. We ask well
 * before expiry and reschedule from the TTL the server hands back, which means
 * a long session never notices the rotation: hls.js picks the new token up on
 * its next request through `withMediaToken`, with nothing reloaded and no gap
 * in playback.
 */
export function useMediaToken(hlsUrl: string | null, expiresInSec: number): MediaTokenState {
  const tokenRef = useRef<string>(hlsUrl ? tokenOf(hlsUrl) : '');
  const [rotation, setRotation] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hlsUrl) return;
    tokenRef.current = tokenOf(hlsUrl);
    setError(null);

    let cancelled = false;
    let timer: number | null = null;

    // Ask at 75% of the lifetime, and never less than 15s out, so a slow
    // network still has room for the round trip before the old token dies.
    const delayFor = (ttlSec: number): number =>
      Math.max(15_000, Math.round(ttlSec * 0.75) * 1000);

    const rotate = async (): Promise<void> => {
      const url = refreshUrlFor(hlsUrl, tokenRef.current);
      if (!url) return;
      try {
        const res = await fetch(url, { credentials: 'include', headers: { accept: 'application/json' } });
        if (!res.ok) throw new Error(String(res.status));
        const data = (await res.json()) as MediaRefreshResponse;
        if (cancelled) return;
        tokenRef.current = data.token;
        setRotation((n) => n + 1);
        setError(null);
        timer = window.setTimeout(() => void rotate(), delayFor(data.expiresInSec));
      } catch {
        if (cancelled) return;
        setError(
          'This video’s playback pass could not be renewed. Reload the page to keep watching — your progress is already saved.',
        );
      }
    };

    if (expiresInSec > 0) {
      timer = window.setTimeout(() => void rotate(), delayFor(expiresInSec));
    }

    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [hlsUrl, expiresInSec]);

  return { tokenRef, rotation, error };
}
